import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function About() {
  const navigate = useNavigate();

  const values = [
    {
      icon: 'ri-target-line',
      title: 'Results-Driven',
      description: 'We focus on delivering measurable results and ROI that directly impact your bottom line.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Transparency',
      description: 'Complete transparency in our processes, reporting, and communication with all clients.'
    },
    {
      icon: 'ri-rocket-line',
      title: 'Innovation',
      description: 'Constantly evolving our strategies with the latest technology and market trends.'
    },
    {
      icon: 'ri-team-line',
      title: 'Partnership',
      description: 'We view every client relationship as a long-term partnership built on mutual success.'
    }
  ];

  const team = [
    {
      name: 'Sarah Mitchell',
      role: 'CEO & Founder',
      description: 'With over 15 years in digital marketing, Sarah has led campaigns generating over $500M in client revenue.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20female%20CEO%20in%20modern%20office%20setting%2C%20confident%20business%20leader%20in%20her%2040s%20wearing%20navy%20blazer%2C%20executive%20portrait%20with%20clean%20background%2C%20sophisticated%20lighting&width=400&height=400&seq=ceo1&orientation=squarish'
    },
    {
      name: 'Michael Chen',
      role: 'VP of Strategy',
      description: 'Former Google Ads specialist with expertise in international markets and high-converting campaign optimization.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Asian%20male%20marketing%20director%20in%20modern%20office%2C%20experienced%20strategist%20in%20his%2030s%20wearing%20dark%20suit%2C%20executive%20headshot%20with%20clean%20professional%20background&width=400&height=400&seq=vp1&orientation=squarish'
    },
    {
      name: 'Emma Rodriguez',
      role: 'Head of AI Solutions',
      description: 'AI and machine learning expert specializing in predictive analytics and automated lead qualification systems.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Hispanic%20female%20AI%20specialist%20in%20tech%20office%20environment%2C%20data%20scientist%20in%20her%2030s%20wearing%20business%20casual%2C%20confident%20portrait%20with%20technology%20background&width=400&height=400&seq=ai-head1&orientation=squarish'
    },
    {
      name: 'David Thompson',
      role: 'Director of Operations',
      description: 'Operations expert ensuring seamless campaign execution and client satisfaction across all international markets.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20male%20operations%20director%20in%20corporate%20office%2C%20experienced%20manager%20in%20his%2040s%20wearing%20business%20attire%2C%20executive%20portrait%20with%20modern%20workplace%20background&width=400&height=400&seq=ops1&orientation=squarish'
    }
  ];

  const achievements = [
    { number: '2018', label: 'Company Founded' },
    { number: '150+', label: 'International Clients' },
    { number: '$500M+', label: 'Client Revenue Generated' },
    { number: '95%', label: 'Client Retention Rate' }
  ];

  const certifications = [
    'Google Ads Certified Partners',
    'Facebook Marketing Partners',
    'Microsoft Advertising Certified',
    'HubSpot Certified Agency',
    'Salesforce Consulting Partners',
    'AWS Advanced Technology Partner'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat py-16 md:py-32"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://readdy.ai/api/search-image?query=Futuristic%20AI%20neural%20network%20visualization%20with%20glowing%20digital%20brain%20patterns%2C%20interconnected%20nodes%20and%20data%20streams%2C%20holographic%20interface%20elements%20with%20blue%20and%20cyan%20lighting%20effects%2C%20modern%20artificial%20intelligence%20graphics%20representing%20data%20processing%20and%20machine%20learning%20algorithms%20on%20dark%20technology%20background&width=1920&height=800&seq=about-ai-hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-4xl">
            <h1 className="text-2xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
              Leading the Future of 
              <span className="text-blue-400"> International Lead Generation</span>
            </h1>
            <p className="text-base md:text-xl text-gray-200 mb-6 md:mb-8 leading-relaxed">
              Since 2018, Halcom Analytics has been helping businesses worldwide generate over $500M in revenue through strategic lead generation and cutting-edge digital marketing solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <div>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-5 md:mb-8">
                Our Story
              </h2>
              <div className="space-y-4 md:space-y-6 text-sm md:text-lg text-gray-600 leading-relaxed">
                <p>
                  Founded in 2018 by industry veterans with over 20 years of combined experience, Halcom Analytics emerged from a simple observation: traditional lead generation methods weren't keeping pace with the rapidly evolving digital landscape.
                </p>
                <p>
                  We recognized that international businesses, particularly in high-value industries like mortgage refinancing, debt consolidation, and home security, needed specialized expertise to navigate complex digital marketing channels effectively.
                </p>
                <p>
                  Today, we're proud to serve 150+ international clients, generating over 2 million qualified leads and driving more than $500M in client revenue. Our success is built on a foundation of innovation, transparency, and relentless focus on measurable results.
                </p>
              </div>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20corporate%20team%20meeting%20in%20glass%20conference%20room%20with%20city%20skyline%20view%2C%20diverse%20group%20of%20professionals%20collaborating%20around%20table%20with%20laptops%20and%20documents%2C%20bright%20natural%20lighting&width=600&height=500&seq=story1&orientation=landscape"
                alt="Our Story"
                className="w-full rounded-2xl shadow-lg object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
              Our Core Values
            </h2>
            <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do and define our approach to client partnerships.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-5 md:p-8 rounded-2xl shadow-lg text-center hover:shadow-xl transition-shadow">
                <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-4 md:mb-6">
                  <i className={`${value.icon} text-xl md:text-2xl text-blue-600`}></i>
                </div>
                <h3 className="text-base md:text-xl font-bold text-gray-900 mb-2 md:mb-4">{value.title}</h3>
                <p className="text-xs md:text-sm text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
              Meet Our Leadership Team
            </h2>
            <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
              Industry experts with decades of experience in digital marketing, AI technology, and international business development.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
            {team.map((member, index) => (
              <div key={index} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src={member.image} 
                  alt={member.name}
                  className="w-full h-40 md:h-80 object-cover object-top"
                />
                <div className="p-3 md:p-6">
                  <h3 className="text-sm md:text-xl font-bold text-gray-900 mb-1 md:mb-2">{member.name}</h3>
                  <p className="text-blue-600 font-semibold mb-2 md:mb-4 text-xs md:text-base">{member.role}</p>
                  <p className="text-gray-600 text-xs leading-relaxed hidden md:block">{member.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-12 md:py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
              Our Achievements
            </h2>
            <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
              Measurable results that demonstrate our commitment to client success and industry leadership.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
            {achievements.map((achievement, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-5xl font-bold text-blue-600 mb-2">{achievement.number}</div>
                <div className="text-gray-600 font-medium text-sm md:text-base">{achievement.label}</div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10 md:mt-16">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8">Industry Certifications & Partnerships</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {certifications.map((cert, index) => (
                <div key={index} className="bg-white p-3 md:p-4 rounded-lg shadow-sm border border-gray-200">
                  <span className="text-gray-700 font-medium text-xs md:text-sm">{cert}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20business%20team%20analyzing%20global%20market%20data%20on%20large%20wall%20displays%20showing%20world%20map%20with%20analytics%2C%20modern%20corporate%20strategy%20room%20with%20international%20focus&width=600&height=500&seq=mission1&orientation=landscape"
                alt="Our Mission"
                className="w-full rounded-2xl shadow-lg object-cover object-top"
              />
            </div>
            <div>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-5 md:mb-8">
                Our Mission
              </h2>
              <div className="space-y-4 md:space-y-6 text-sm md:text-lg text-gray-600 leading-relaxed">
                <p>
                  To empower international businesses with cutting-edge lead generation strategies that drive sustainable growth and measurable ROI. We believe that every business deserves access to world-class digital marketing expertise, regardless of size or location.
                </p>
                <p>
                  Through innovative technology, data-driven insights, and personalized service, we help our clients navigate the complex digital landscape and achieve their growth objectives in competitive international markets.
                </p>
              </div>
              <div className="mt-6 md:mt-8">
                <Button onClick={() => navigate('/contact')}>
                  <i className="ri-handshake-line mr-2"></i>
                  Partner With Us
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">
            Ready to Work Together?
          </h2>
          <p className="text-base md:text-xl text-blue-100 mb-6 md:mb-8">
            Join the 150+ international clients who trust Halcom Analytics to drive their lead generation success.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-white text-blue-600 hover:bg-gray-100"
            >
              <i className="ri-phone-line mr-2"></i>
              Start Your Journey
            </Button>
            <Button 
              variant="outline"
              size="lg"
              onClick={() => navigate('/services')}
              className="border-white text-white hover:bg-white hover:text-blue-600"
            >
              <i className="ri-service-line mr-2"></i>
              Explore Services
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
